import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchReadingMilestones,
  deleteReadingMilestone,
  updateReadingMilestone,
} from "@/api/reading-milestones";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Trophy,
  Edit,
  Trash2,
  Search,
  Clock,
  BookOpen,
  Headphones,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { ReadingMilestone } from "@/types/readingMilestones";

const ReadingMilestonesList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [milestoneToDelete, setMilestoneToDelete] = useState<number | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");

  const { data: milestones = [], isLoading } = useQuery({
    queryKey: ["readingMilestones"],
    queryFn: fetchReadingMilestones,
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => {
      const milestone = milestones.find((m) => m.id === id);
      return updateReadingMilestone(id, { isActive: !milestone?.isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["readingMilestones"] });
      toast({
        title: "Milestone status updated",
        description: "The milestone status has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteReadingMilestone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["readingMilestones"] });
      toast({
        title: "Milestone deleted",
        description: "The reading milestone has been deleted successfully.",
      });
      setIsDeleteDialogOpen(false);
      setMilestoneToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDelete = (id: number) => {
    setMilestoneToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (milestoneToDelete) {
      deleteMutation.mutate(milestoneToDelete);
    }
  };

  const filterMilestones = (items: ReadingMilestone[]) => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.description?.toLowerCase().includes(q)
    );
  };

  if (isLoading) {
    return (
      <AdminLayout title="Reading Rewards">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </AdminLayout>
    );
  }

  const activeMilestones = filterMilestones(
    milestones.filter((m) => m.isActive)
  );
  const inactiveMilestones = filterMilestones(
    milestones.filter((m) => !m.isActive)
  );

  return (
    <AdminLayout title="Reading Rewards">
      <div className="mb-4 flex justify-between items-center">
        <p className="text-muted-foreground text-sm">
          Reward users who reach reading and listening milestones with
          promotions and notifications
        </p>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search milestones by name..."
          className="pl-9 h-9"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="space-y-1">
            <DialogTitle>Delete Milestone</DialogTitle>
            <DialogDescription className="text-xs">
              Are you sure you want to delete this milestone? Users who already
              achieved it will lose the record. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-1 sm:gap-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <span className="mr-2">Deleting...</span>
                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="w-full grid grid-cols-2 h-10">
          <TabsTrigger value="active" className="text-sm">
            Active
            <Badge className="ml-2 bg-green-100 text-green-700 border-none">
              {activeMilestones.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="inactive" className="text-sm">
            Inactive
            <Badge className="ml-2 bg-gray-100 text-gray-700 border-none">
              {inactiveMilestones.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          {activeMilestones.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 text-sm">
              {searchQuery
                ? "No matching active milestones found."
                : "No active milestones found."}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeMilestones.map((m) => (
                <MilestoneCard
                  key={m.id}
                  milestone={m}
                  onToggleActive={() => toggleMutation.mutate(m.id)}
                  onEdit={() => navigate(`/admin/reading-rewards/${m.id}`)}
                  onDelete={() => handleDelete(m.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inactive" className="mt-4">
          {inactiveMilestones.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 text-sm">
              {searchQuery
                ? "No matching inactive milestones found."
                : "No inactive milestones found."}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inactiveMilestones.map((m) => (
                <MilestoneCard
                  key={m.id}
                  milestone={m}
                  onToggleActive={() => toggleMutation.mutate(m.id)}
                  onEdit={() => navigate(`/admin/reading-rewards/${m.id}`)}
                  onDelete={() => handleDelete(m.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

// ── Helper: format minutes to human-readable ──────────────────

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ── Helper: time source label ─────────────────────────────────

function timeSourceLabel(source: string): {
  label: string;
  icon: React.ReactNode;
} {
  switch (source) {
    case "reading":
      return { label: "Reading", icon: <BookOpen className="h-3 w-3" /> };
    case "listening":
      return { label: "Listening", icon: <Headphones className="h-3 w-3" /> };
    default:
      return { label: "Either", icon: <Clock className="h-3 w-3" /> };
  }
}

// ── Milestone Card ────────────────────────────────────────────

type MilestoneCardProps = {
  milestone: ReadingMilestone;
  onToggleActive: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

const MilestoneCard = ({
  milestone,
  onToggleActive,
  onEdit,
  onDelete,
}: MilestoneCardProps) => {
  const ts = timeSourceLabel(milestone.timeSource);

  return (
    <Card
      className={`${
        !milestone.isActive ? "opacity-70" : ""
      } overflow-hidden border shadow-sm flex flex-col h-full`}
    >
      <CardHeader className="p-4 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 border-none">
              <Trophy className="h-3 w-3 mr-1" />
              {formatMinutes(milestone.thresholdMinutes)}
            </Badge>
            <Badge
              variant="outline"
              className="flex items-center gap-1 text-xs"
            >
              {ts.icon}
              {ts.label}
            </Badge>
          </div>
          <Badge
            variant="outline"
            className={
              milestone.isActive
                ? "bg-green-100 text-green-700 border-green-200"
                : "bg-gray-100 text-gray-700 border-gray-200"
            }
          >
            {milestone.isActive ? "Active" : "Inactive"}
          </Badge>
        </div>
        <CardTitle className="text-base font-medium">{milestone.name}</CardTitle>
        {milestone.description && (
          <CardDescription className="text-sm mt-1 line-clamp-2">
            {milestone.description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="p-4 pt-0 flex-grow">
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
          <span className="text-muted-foreground">Promotion:</span>
          <span className="text-right font-medium truncate">
            {milestone.promotionName || "None"}
          </span>

          <span className="text-muted-foreground">Notification:</span>
          <span className="text-right font-medium truncate">
            {milestone.notificationTemplateName || "None"}
          </span>

          <span className="text-muted-foreground">Achievers:</span>
          <span className="text-right font-medium flex items-center justify-end gap-1">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            {milestone.achieverCount ?? 0}
          </span>

          <span className="text-muted-foreground">Sort order:</span>
          <span className="text-right font-medium">{milestone.sortOrder}</span>
        </div>
      </CardContent>
      <CardFooter className="p-3 flex justify-end gap-2 bg-muted/10 border-t">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-3"
          onClick={onToggleActive}
        >
          {milestone.isActive ? "Deactivate" : "Activate"}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onEdit}
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ReadingMilestonesList;
